// Purpose: MetricTile — the headline stat blocks on the admin Dashboard
// ("Active Shares: 124", "Pending Approvals: 3", "Shares expiring in
// 24h: 12"). Composable; either pass `value`/`label` directly or pass
// children for a custom composition.
//
// Visual: Card with accent-colored label line on top, large
// display-m value number, optional trend indicator + footnote below.

import type { ReactNode } from 'react';
import { Card } from './Card.tsx';

export type MetricTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export interface MetricTileProps {
  label: ReactNode;
  value: ReactNode;
  tone?: MetricTone;
  /** Optional leading icon, rendered next to the label. */
  icon?: ReactNode;
  /** Optional small text under the value. */
  footnote?: ReactNode;
  /** Optional trend indicator (e.g., "+12% vs last week"). */
  trend?: ReactNode;
  className?: string;
}

const TONE_TEXT: Record<MetricTone, string> = {
  neutral: 'text-fg-muted',
  success: 'text-status-success',
  warning: 'text-status-warning',
  danger: 'text-status-danger',
  info: 'text-accent-primary',
};

export function MetricTile({
  label,
  value,
  tone = 'neutral',
  icon,
  footnote,
  trend,
  className,
}: MetricTileProps) {
  return (
    <Card className={['flex flex-col gap-3', className ?? ''].join(' ')}>
      <div className={['flex items-center gap-2 text-label uppercase', TONE_TEXT[tone]].join(' ')}>
        {icon}
        <span>{label}</span>
      </div>
      <div className="font-display text-display-m text-fg-primary [font-feature-settings:var(--font-feature-display)]">
        {value}
      </div>
      {trend ? <div className="text-body-s text-fg-muted">{trend}</div> : null}
      {footnote ? <div className="text-body-s text-fg-muted">{footnote}</div> : null}
    </Card>
  );
}
